import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import PromptTagEditor from '../components/PromptTagEditor';
import PromptTagReferenceLabel from '../components/PromptTagReferenceLabel';
import {
  QUESTIONS_TAG,
  buildExtraPromptTagReferences,
  buildQuestionTagReference,
  dedupeTagReferences,
  getMismatchedExtraPromptTagWarning,
  tagReferencesToKnownTags,
} from '../lib/promptTagReferences';

const AGENT_API_BASE_URL = process.env.REACT_APP_AGENT_API_URL || '';
const ANALYTICS_API_URL = AGENT_API_BASE_URL ? `${AGENT_API_BASE_URL}/api` : '/api';
const KNOWN_PROMPT_TAGS = [];
const KNOWN_PROMPT_TAG_PATTERNS = [/^<sentiment_[^<>]+>$/i];

const mergeKnownTags = (...groups) => {
  const seen = new Set();
  const tags = [];

  groups
    .flat()
    .forEach((value) => {
      const tag = String(value || '').trim();
      if (!tag) return;
      const normalizedTag = tag.toLowerCase();
      if (seen.has(normalizedTag)) return;
      seen.add(normalizedTag);
      tags.push(tag);
    });

  return tags;
};

const buildLocalTemplateId = () => (
  `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
);

const normalizeTemplate = (template, analysisType) => {
  const resolvedId = String(template?.templateId || '').trim();
  const name = String(template?.name || '').trim();
  return {
    templateId: resolvedId || buildLocalTemplateId(),
    analysisType,
    name,
    prompt: String(template?.prompt || ''),
    isPersisted: !!resolvedId,
  };
};

const createEmptyTemplate = (analysisType) => ({
  templateId: buildLocalTemplateId(),
  analysisType,
  name: '',
  prompt: '',
  isPersisted: false,
});

const mapAnalysisTypeOptions = (types, { source, prefix = '' }) => (
  Object.values(types || {})
    .map((item) => ({
      id: String(item?.id || item?.name || '').trim().toLowerCase(),
      label: String(item?.label || '').trim(),
      configurable: !!item?.settings?.configurable,
      source,
      optionLabel: `${prefix}${String(item?.label || '').trim() || String(item?.id || item?.name || '').trim()}`
    }))
    .filter((item) => item.id)
);

const AnalyticsSetupPagePromptTemplates = ({ mainAnalysisTypes, extraAnalysisTypes }) => {
  const analysisTypeOptions = useMemo(() => {
    const merged = [
      ...mapAnalysisTypeOptions(mainAnalysisTypes, { source: 'main' }),
      ...mapAnalysisTypeOptions(extraAnalysisTypes, { source: 'extra', prefix: 'Extra: ' }),
    ].filter((item) => item.configurable && item.id !== 'none');

    const deduped = [];
    const seenIds = new Set();
    merged.forEach((item) => {
      if (seenIds.has(item.id)) return;
      seenIds.add(item.id);
      deduped.push(item);
    });
    return deduped;
  }, [extraAnalysisTypes, mainAnalysisTypes]);

  const analysisTypeLabelMap = useMemo(() => (
    analysisTypeOptions.reduce((accumulator, item) => {
      accumulator[item.id] = item.label || item.id;
      return accumulator;
    }, {})
  ), [analysisTypeOptions]);

  const [selectedAnalysisType, setSelectedAnalysisType] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateDraft, setTemplateDraft] = useState({ name: '', prompt: '' });

  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [deleteState, setDeleteState] = useState('idle');

  const [loadError, setLoadError] = useState('');
  const [editorError, setEditorError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    templateId: '',
    templateName: '',
  });
  const [discardModalOpen, setDiscardModalOpen] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.templateId === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );
  const selectedAnalysisTypeOption = useMemo(
    () => analysisTypeOptions.find((option) => option.id === selectedAnalysisType) || null,
    [analysisTypeOptions, selectedAnalysisType]
  );
  const isExtraAnalysisType = selectedAnalysisTypeOption?.source === 'extra';
  const isSurveyAnalysisType = selectedAnalysisType === 'survey';
  const selectedAnalysisTypeLabel = selectedAnalysisTypeOption?.label || selectedAnalysisType || 'Analysis';

  const promptTagReferences = useMemo(() => (
    dedupeTagReferences(
      isSurveyAnalysisType ? [buildQuestionTagReference(QUESTIONS_TAG)] : [],
      isExtraAnalysisType
        ? buildExtraPromptTagReferences({
          promptText: templateDraft.prompt,
          analysisLabel: selectedAnalysisTypeLabel
        })
        : []
    )
  ), [
    isExtraAnalysisType,
    isSurveyAnalysisType,
    selectedAnalysisTypeLabel,
    templateDraft.prompt
  ]);

  const promptEditorKnownTags = useMemo(() => (
    mergeKnownTags(KNOWN_PROMPT_TAGS, tagReferencesToKnownTags(promptTagReferences))
  ), [promptTagReferences]);

  const extraPromptTagWarning = useMemo(() => (
    isExtraAnalysisType ? getMismatchedExtraPromptTagWarning(templateDraft.prompt) : ''
  ), [isExtraAnalysisType, templateDraft.prompt]);

  const draftDirty = useMemo(() => {
    if (!selectedTemplate) return false;
    return (
      templateDraft.name !== selectedTemplate.name
      || templateDraft.prompt !== selectedTemplate.prompt
    );
  }, [selectedTemplate, templateDraft.name, templateDraft.prompt]);

  const applyTemplateSelection = useCallback((template) => {
    if (!template) {
      setSelectedTemplateId('');
      setTemplateDraft({ name: '', prompt: '' });
      return;
    }
    setSelectedTemplateId(template.templateId);
    setTemplateDraft({
      name: template.name,
      prompt: template.prompt,
    });
  }, []);

  const loadTemplates = useCallback(async (analysisType) => {
    if (!analysisType) {
      setTemplates([]);
      applyTemplateSelection(null);
      return;
    }

    setLoadingTemplates(true);
    setLoadError('');
    setEditorError('');
    setSaveState('idle');

    try {
      const { data } = await api.get('/analysis-prompt-templates', {
        params: { analysisType },
        baseURL: ANALYTICS_API_URL,
      });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to load prompt templates');
      }

      const items = Array.isArray(data?.templates)
        ? data.templates.map((template) => normalizeTemplate(template, analysisType))
        : [];
      const preferredTemplate = items[0] || null;

      setTemplates(items);
      applyTemplateSelection(preferredTemplate);
    } catch (error) {
      setTemplates([]);
      applyTemplateSelection(null);
      setLoadError(error?.message || 'Failed to load prompt templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, [applyTemplateSelection]);

  useEffect(() => {
    if (!analysisTypeOptions.length) {
      setSelectedAnalysisType('');
      setTemplates([]);
      applyTemplateSelection(null);
      return;
    }

    setSelectedAnalysisType((currentType) => (
      analysisTypeOptions.some((option) => option.id === currentType)
        ? currentType
        : analysisTypeOptions[0].id
    ));
  }, [analysisTypeOptions, applyTemplateSelection]);

  useEffect(() => {
    if (!selectedAnalysisType) return;
    loadTemplates(selectedAnalysisType);
  }, [selectedAnalysisType, loadTemplates]);

  const handleAnalysisTypeChange = (nextType) => {
    setSelectedAnalysisType(nextType);
  };

  const handleOpenConfigureTemplate = (templateId) => {
    const nextTemplate = templates.find((template) => template.templateId === templateId);
    applyTemplateSelection(nextTemplate || null);
    setEditorError('');
    setDeleteError('');
    setSaveState('idle');
  };

  const handleCloseTemplateEditor = useCallback(() => {
    applyTemplateSelection(null);
    setEditorError('');
    setSaveState('idle');
    setDiscardModalOpen(false);
  }, [applyTemplateSelection]);

  const requestCloseTemplateEditor = () => {
    if (!draftDirty) {
      handleCloseTemplateEditor();
      return;
    }
    setDiscardModalOpen(true);
  };

  const handleAddTemplate = () => {
    if (!selectedAnalysisType) return;
    const newTemplate = createEmptyTemplate(selectedAnalysisType);
    setTemplates((prev) => [...prev, newTemplate]);
    applyTemplateSelection(newTemplate);
    setEditorError('');
    setDeleteError('');
    setSaveState('idle');
  };

  const closeDeleteModal = () => {
    if (deleteState === 'deleting') return;
    setDeleteModal({ open: false, templateId: '', templateName: '' });
    setDeleteError('');
  };

  const openDeleteModal = (template) => {
    setDeleteModal({
      open: true,
      templateId: template.templateId,
      templateName: template.name || 'Untitled template',
    });
    setDeleteError('');
  };

  const applyTemplateRemoval = useCallback((templateIdToRemove) => {
    const remainingTemplates = templates.filter((item) => item.templateId !== templateIdToRemove);
    const nextSelectedTemplate = remainingTemplates.find((item) => item.templateId === selectedTemplateId)
      || remainingTemplates[0]
      || null;

    setTemplates(remainingTemplates);
    applyTemplateSelection(nextSelectedTemplate);
  }, [applyTemplateSelection, selectedTemplateId, templates]);

  const handleConfirmDelete = async () => {
    if (!deleteModal.templateId || !selectedAnalysisType) return;

    const template = templates.find((item) => item.templateId === deleteModal.templateId);
    if (!template) {
      closeDeleteModal();
      return;
    }

    setDeleteState('deleting');
    setDeleteError('');
    setEditorError('');

    try {
      if (template.isPersisted) {
        const { data } = await api.post('/analysis-prompt-templates/delete', {
          analysisType: selectedAnalysisType,
          templateId: template.templateId,
        }, { baseURL: ANALYTICS_API_URL });

        if (!data?.success) {
          throw new Error(data?.message || 'Failed to delete prompt template');
        }
      }

      applyTemplateRemoval(template.templateId);
      setDeleteModal({ open: false, templateId: '', templateName: '' });
      setDeleteState('idle');
    } catch (error) {
      setDeleteState('idle');
      setDeleteError(error?.message || 'Failed to delete prompt template');
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedAnalysisType || !selectedTemplate) return;

    const normalizedName = templateDraft.name.trim();
    if (!normalizedName) {
      setEditorError('Template name is required.');
      return;
    }

    setSaveState('saving');
    setEditorError('');

    try {
      const { data } = await api.post('/analysis-prompt-templates/save', {
        analysisType: selectedAnalysisType,
        templateId: selectedTemplate.isPersisted ? selectedTemplate.templateId : undefined,
        name: normalizedName,
        prompt: templateDraft.prompt,
      }, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to save prompt template');
      }

      const savedTemplate = normalizeTemplate(data?.template || {}, selectedAnalysisType);
      setTemplates((prev) => {
        const selectedIndex = prev.findIndex((item) => item.templateId === selectedTemplate.templateId);
        if (selectedIndex < 0) return [...prev, savedTemplate];
        const next = [...prev];
        next[selectedIndex] = savedTemplate;
        return next;
      });
      applyTemplateSelection(savedTemplate);
      setSaveState('saved');
      window.setTimeout(() => setSaveState('idle'), 2000);
    } catch (error) {
      setSaveState('idle');
      setEditorError(error?.message || 'Failed to save prompt template');
    }
  };

  return (
    <>
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-[auto_minmax(260px,1fr)_auto] md:items-center">
          <label className="text-sm font-semibold text-gray-700" htmlFor="analysis-template-type-select">
            Main analysis type
          </label>
          <select
            id="analysis-template-type-select"
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedAnalysisType}
            onChange={(event) => handleAnalysisTypeChange(event.target.value)}
            disabled={!analysisTypeOptions.length || loadingTemplates}
          >
            {analysisTypeOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.optionLabel || option.label || option.id}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleAddTemplate}
            disabled={!selectedAnalysisType || loadingTemplates}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-lg font-semibold transition ${
              !selectedAnalysisType || loadingTemplates
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
            title="Add template"
          >
            +
          </button>
        </div>

        {loadingTemplates && (
          <div className="text-xs text-gray-400">Loading prompt templates...</div>
        )}

        {loadError && (
          <div className="text-sm text-red-600">{loadError}</div>
        )}

        {!loadError && !loadingTemplates && templates.length === 0 && (
          <div className="text-sm text-gray-500">
            No templates for this analysis type yet. Click + to add one.
          </div>
        )}

        {!loadError && templates.length > 0 && (
          <div className="space-y-2">
            {templates.map((template, index) => {
              const isActive = template.templateId === selectedTemplateId;
              return (
                <div
                  key={template.templateId}
                  className={`grid gap-2 rounded-lg border p-3 md:grid-cols-[minmax(180px,1fr)_auto_auto] md:items-center ${
                    isActive ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 bg-gray-50/40'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-800">
                      {template.name || `Template ${index + 1}`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {analysisTypeLabelMap[template.analysisType] || template.analysisType}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOpenConfigureTemplate(template.templateId)}
                    disabled={loadingTemplates}
                    className={`inline-flex w-fit items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                      loadingTemplates
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  >
                    Configure
                  </button>
                  <button
                    type="button"
                    onClick={() => openDeleteModal(template)}
                    disabled={loadingTemplates || deleteState === 'deleting'}
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-lg font-semibold transition ${
                      loadingTemplates || deleteState === 'deleting'
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-rose-600 text-white hover:bg-rose-700'
                    }`}
                    title="Remove template"
                  >
                    -
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {selectedTemplate && (
        <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Configure Template
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={loadingTemplates || saveState === 'saving' || !draftDirty}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  loadingTemplates || saveState === 'saving' || !draftDirty
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {saveState === 'saving' ? 'Saving...' : 'Save changes'}
              </button>

              <button
                type="button"
                onClick={requestCloseTemplateEditor}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>

          {saveState === 'saved' && (
            <div className="text-sm text-emerald-600">Saved successfully.</div>
          )}

          {editorError && (
            <div className="text-sm text-red-600">{editorError}</div>
          )}

          <div className="grid gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700" htmlFor="analysis-template-name">
                Template name
              </label>
              <input
                id="analysis-template-name"
                type="text"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={templateDraft.name}
                onChange={(event) => setTemplateDraft((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Ex: Post-call summary template"
                disabled={loadingTemplates}
              />
            </div>

            <div className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
              <h3 className="text-base font-semibold text-gray-900">Prompt Template</h3>
              <PromptTagReferenceLabel items={promptTagReferences} />
              {extraPromptTagWarning && (
                <div className="text-xs text-amber-700">
                  {extraPromptTagWarning}
                </div>
              )}
              <PromptTagEditor
                value={templateDraft.prompt}
                onChange={(nextValue) => setTemplateDraft((prev) => ({ ...prev, prompt: nextValue }))}
                disabled={loadingTemplates}
                knownTags={promptEditorKnownTags}
                knownTagPatterns={KNOWN_PROMPT_TAG_PATTERNS}
                minHeightClassName="min-h-[380px]"
              />
            </div>
          </div>
        </section>
      )}

      {deleteModal.open && (
        <div className="fixed inset-0 z-[81] flex items-center justify-center bg-slate-900/55 px-4">
          <div className="w-full max-w-lg rounded-xl border border-rose-200 bg-white shadow-2xl">
            <div className="border-b border-rose-100 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">Delete Prompt Template</h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">
                Are you sure you want to delete "{deleteModal.templateName}"?
              </p>
              {deleteError && (
                <div className="text-sm text-red-600">{deleteError}</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleteState === 'deleting'}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  deleteState === 'deleting'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteState === 'deleting'}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  deleteState === 'deleting'
                    ? 'bg-rose-300 text-white cursor-not-allowed'
                    : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
              >
                {deleteState === 'deleting' ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {discardModalOpen && (
        <div className="fixed inset-0 z-[82] flex items-center justify-center bg-slate-900/55 px-4">
          <div className="w-full max-w-lg rounded-xl border border-amber-200 bg-white shadow-2xl">
            <div className="border-b border-amber-100 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">Discard Changes</h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">
                Are you sure you want to discard changes?
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setDiscardModalOpen(false)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCloseTemplateEditor}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnalyticsSetupPagePromptTemplates;
